import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';

export const Shell = styled.div`
    display: flex;
    min-height: 100vh;
    /* Fond explicite : ne dépend pas de la propagation depuis body */
    background: ${(p) => p.theme.ground};
    color: ${(p) => p.theme.text};

    /* Dimensions du shell exposées aux pages : un panneau en plein écran peut
       ainsi rester dans la zone de contenu sans coder en dur la largeur de la
       sidebar, et suit automatiquement son repli. */
    --shell-topbar: 54px;
    --shell-sidebar: ${(p) => (p.$collapsed ? '64px' : '236px')};
`;

export const Sidebar = styled.nav`
    width: var(--shell-sidebar);
    flex-shrink: 0;
    position: sticky;
    top: 0;
    height: 100vh;
    overflow-y: auto;
    background: ${(p) => p.theme.surface2};
    border-right: 1px solid ${(p) => p.theme.border};
    display: flex;
    flex-direction: column;
    padding: 14px 10px;
    gap: 2px;
    transition: width 0.18s ease;

    ${(p) => p.$collapsed && css`
        .nav-label, .nav-group-title, .brand-name, .user-meta {
            display: none;
        }
    `}
`;

export const Brand = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px 14px;
    border-bottom: 1px solid ${(p) => p.theme.border};
    margin-bottom: 10px;

    a {
        display: flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        color: inherit;
        min-width: 0;
    }
`;

export const BrandLogo = styled.div`
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: ${(p) => p.theme.radius.sm};
    background: linear-gradient(135deg, ${(p) => p.theme.accent} 0%, #61B0EE 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 800;
    font-size: 0.95rem;
`;

export const BrandName = styled.div`
    font-weight: 800;
    font-size: 0.9rem;
    line-height: 1.15;
    white-space: nowrap;

    small {
        display: block;
        font-weight: 400;
        font-size: 0.7rem;
        color: ${(p) => p.theme.text3};
    }
`;

export const NavGroupTitle = styled.div`
    font-size: ${(p) => p.theme.type.label};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${(p) => p.theme.text3};
    padding: 12px 10px 4px;
`;

/* NavLink v6 ajoute la classe .active automatiquement */
export const NavItem = styled(NavLink)`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: ${(p) => p.theme.radius.sm};
    color: ${(p) => p.theme.text2};
    text-decoration: none;
    font-weight: 600;
    font-size: 0.85rem;
    white-space: nowrap;
    transition: background 0.12s ease, color 0.12s ease;

    i {
        font-size: 1.15rem;
        flex-shrink: 0;
    }

    &:hover {
        background: ${(p) => p.theme.surface3};
        color: ${(p) => p.theme.text};
    }

    &.active {
        background: ${(p) => p.theme.accentSoft};
        color: ${(p) => p.theme.accentText};

        i { color: ${(p) => p.theme.accent}; }
    }
`;

export const SidebarFooter = styled.div`
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid ${(p) => p.theme.border};
`;

export const UserCard = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
`;

export const Avatar = styled.div`
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 50%;
    background: ${(p) => p.theme.accentSoft};
    color: ${(p) => p.theme.accentText};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 800;
`;

export const UserMeta = styled.div`
    min-width: 0;

    .name {
        font-size: 0.78rem;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const Main = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

export const Topbar = styled.header`
    height: 54px;
    flex-shrink: 0;
    background: ${(p) => p.theme.surface};
    border-bottom: 1px solid ${(p) => p.theme.border};
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 20px;
    position: sticky;
    top: 0;
    z-index: 5;

    h1 {
        margin: 0;
        font-size: 1rem;
        font-weight: 800;
    }
`;

export const TopbarSpacer = styled.div`
    flex: 1;
`;

export const Content = styled.div`
    padding: 20px 24px;
    width: 100%;
`;

/* ═══ Écran de connexion ═══ */
export const LoginContainer = styled.div`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => p.theme.ground};
`;

export const LoginCard = styled.div`
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow: ${(p) => p.theme.shadowMd};
    padding: 40px 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    max-width: 400px;

    img {
        max-width: 260px;
    }
    p {
        margin: 0;
        color: ${(p) => p.theme.text2};
        font-size: ${(p) => p.theme.type.caption};
        text-align: center;
    }
`;
